import { SOFT_TEXT_CLASSES, cn } from '@mustauto/ui/web';
import { formatDayCount, inShopDoneNote } from '@mustauto/types/in-shop';
import { Badge } from '@/components/ui/badge';
import { formatDeadline } from '@/components/OSDeadlineField';
import type { InShopEntry } from '@/hooks/useCarsInShop';

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** "Hoje, 18:00", "Amanhã, 18:00", "Ontem, 18:00" ou "seg., 02/11, 12:00" — no fuso de quem lê. */
function deadlineLabel(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const time = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const shifted = (days: number) => {
    const x = new Date(now);
    x.setDate(x.getDate() + days);
    return x;
  };
  if (sameDay(d, now)) return `Hoje, ${time}`;
  if (sameDay(d, shifted(1))) return `Amanhã, ${time}`;
  if (sameDay(d, shifted(-1))) return `Ontem, ${time}`;
  return formatDeadline(iso);
}

/**
 * O prazo de um carro na lista. Vermelho só quando passou e o serviço anda;
 * com o serviço terminado, o prazo fica apagado e vira histórico — o carro
 * pronto esperando o dono não é atraso da oficina.
 */
export function InShopDeadline({ entry }: { entry: InShopEntry }) {
  const { deadline, deadlineStatus, deadlineDaysLate } = entry;

  if (!deadline) {
    return <span className="text-muted-foreground">{deadlineStatus === 'done' ? '—' : 'Sem prazo'}</span>;
  }

  const label = deadlineLabel(deadline);

  switch (deadlineStatus) {
    case 'late':
      return (
        <div className="whitespace-nowrap">
          <Badge variant="destructive">
            {deadlineDaysLate === 0 ? 'Atrasado' : `Atrasado ${formatDayCount(deadlineDaysLate)}`}
          </Badge>
          <p className="mt-1 text-xs text-muted-foreground">era {label.charAt(0).toLowerCase() + label.slice(1)}</p>
        </div>
      );
    case 'today':
      return <span className={cn('whitespace-nowrap font-semibold', SOFT_TEXT_CLASSES.amber)}>{label}</span>;
    case 'done':
      return (
        <div className="whitespace-nowrap text-muted-foreground">
          {label}
          <p className="text-xs">{inShopDoneNote(entry)}</p>
        </div>
      );
    default:
      return <span className="whitespace-nowrap">{label}</span>;
  }
}
